class Chart {
  constructor(config) {
    this.container = config.container;
    this.data = config.data || [];
    this.series = config.series || [];
    this.xAxis = config.xAxis || {};
    this.yAxis = config.yAxis || {};
    this.events = config.events || {};
    this.heightScale = config.heightScale || 0.2;
    this.widthScale = config.widthScale || 0.98;
    this.scaleConfig = {
      scaleLinear: d3.scaleLinear(),
      scaleTime: d3.scaleTime(),
    },
    this.dateConfig = {
      'MM/YY': d3.timeFormat('%m/%y'),
      'MM/YYYY': d3.timeFormat('%m/%Y'),
      'MM/DD/YYYY': d3.timeFormat('%b %d %Y'),
      'MM/DD': d3.timeFormat('%b %d'),
      YYYY: d3.timeFormat('%Y'),
      timestamp: d3.timeFormat('%b %d %I:%M %p'),
    };
  }

  // This method is used to ensure that all required property types are provide
  checkConfigProp(prop) {
    if (typeof this[prop] === 'undefined') {
      const err = new Error(`Error: Please provide a value for the "${prop}" config option `);
      throw err;
    }
  }

  validateConfig() {
    if (typeof this.requiredProps !== 'undefined') {
      this.requiredProps.forEach(prop => this.checkConfigProp(prop));
    }
    return this;
  }

  /**
   * This method is used to set data for the Heatmap
   * @param {array} data [data for heatmap]
   */
  setData(data) {
    this.data = data;
    return this;
  }

  /**
   * This method is used to set the chart dimensions
   * @param {string} selector  [CSS selector for chart]
   * @param {object} [margin] [margins to apply]
   * @return {object} [chart dimensions]
   */
  setChartDimensions(selector = this.container, margin = { top: 15, right: 15, bottom: 25, left: 50 }) {
    let width = d3.select(selector).style('width');
    width = parseInt(width.substring(0, width.length - 2)) * this.widthScale;

    let height = width * this.heightScale;

    // Subtract margins from width and height
    width = width - margin.left - margin.right,
    height = height - margin.top - margin.bottom;

    const chartDimensions = {
      margin,
      width,
      height,
    };
    this.chartDimensions = chartDimensions;
    return chartDimensions;
  }

  /**
   * This method is used to calculate the min/max values for the upstream to downstream dataset
   * The method collapses the values across all value fields into an array for each record
   * That array is then flattened into a single array that the d3.extent method is then run on
   * @param  {array} data [data to evaluate]
   * @return {array}      [returns an array of the min and max]
   */
  calcExtent(data, field) {
    const min = d3.min(data, d => d[field]);
    const max = d3.max(data, d => d[field]);
    // if ( min > 0 ) min = 0;
    return [min, max];
  }

  /**
   * This method is used to remove an existing Heatmap
   * @return {object} [returns Heatmap]
   */
  removeSVG(message = '') {
    document.querySelector(this.container).innerHTML = message;
    return this;
  }

  /**
   * This method is used to create the SVG that all of the other chart elements will be appended to
   * @return {object} [returns Heatmap]
   */
  createSVG() {
    const chartDimensions = this.setChartDimensions();
    const { width, height, margin } = chartDimensions;

    this.removeSVG();
    const svg = d3.select(this.container)
      .append('svg')
        .attr('width', width + margin.left + margin.right)
        .attr('height', height + 20 )
        .attr('id', 'd3-graph')
        .style('overflow', 'visible')
      .append('g')
        .attr('transform', `translate(${margin.left},${margin.top})`);
    this.svg = svg;
    return this;
  } // createSVG

  /**
   * This method is used to create the scales for the D3 graph
   * i.e. scaleTime, scaleLinear
   * @return {object}   [returns this]
   */
  createScales() {
    const { width, height } = this.chartDimensions;
    const { data } = this;
    const xScale = this.xAxis.scale;
    const yScale = this.yAxis.scale;
    const xAxisField = this.xAxis.field || 'rdate';
    const yAxisField = this.yAxis.field || 'rvalue';
    const xAxisFormat = this.xAxis.format || 'mm/dd/yyyy';

    // Set X and Y ranges
    const x = this.scaleConfig[xScale].range([5, width-5]);
    const y = this.scaleConfig[yScale].range([height, 0]);

    // Set X and Y domains aka the min/max x and y axis values
    if (xAxisFormat.toLowerCase() === 'timestamp') {
      data.sort((a,b) => {
        if (a[xAxisField] > b[xAxisField]) return 1;
        if (a[xAxisField] < b[xAxisField]) return -1;
        return 0;
      });
      x.domain(d3.extent(data, d => new Date(d[xAxisField])));
    } else {
      x.domain(d3.extent(data, d => new Date(`${d[xAxisField]} 00:00:00`)));
    }
    y.domain(this.calcExtent(data, yAxisField));
    this.x = x;
    this.y = y;
    return this;
  } // createScales()

  /**
   * This method is used to create a x or y axis
   * @param  {string}  type                [axis type i.e. X or Y]
   * @param  {string} axisField           [Field that to be used for axis labels]
   * @param  {String}  [axisClass='label'] [css class(es) to apply to axis]
   * @return {object}                     [returns Heatmap]
   */
  createAxis(type, axisField, axisClass) {
    const { width, height } = this.chartDimensions;
    const { svg, x, y } = this;

    if (type === 'x') {
      const xAxisFormat = this.xAxis.format || 'mm/dd/yyyy';
      const tickFormat = this.dateConfig[xAxisFormat];
      const tickCount = typeof this.xAxis.ticks === 'undefined' ? 8 : this.xAxis.ticks;

      const xAxis = g => g
        .attr('class', axisClass)
        .attr('transform', `translate(0,${height})`)
        .call(d3.axisBottom(x).ticks(tickCount).tickFormat(tickFormat));

      // Create the x axis gridlines
      const xAxisGridlines = g => g
        .attr('class', 'x-axis grid')
        .attr('transform', `translate(0,${height})`)
        .call(d3.axisBottom(x).ticks(tickCount).tickSize(-height).tickFormat(''));

      svg.append('g').call(xAxisGridlines);
      svg.append('g').call(xAxis);
    } else if (type === 'y') {
      const tickCount = typeof this.yAxis.ticks === 'undefined' ? 8 : this.yAxis.ticks;

      const yAxis = g => g
        .attr('class', axisClass)
        .call(d3.axisLeft(y).ticks(tickCount));

      const yAxisGridlines = g => g
        .attr('class', 'y-axis grid')
        .call(d3.axisLeft(y).ticks(tickCount).tickSize(-width).tickFormat(''));

      svg.append('g').call(yAxisGridlines);
      svg.append('g').call(yAxis);
    }
    return this;
  } // createAxis()

  /**
   * This utility method us used to create an D3 line series constructor
   * This method relies on the calcy() utility method to create a stacked line chart
   * @param  {array} xField [field to be used for x value ]
   * @param  {array} yField [field to be used for y value ]
   * @param  {object} x [x scale]
   * @param  {object} y [y scale]
   * @return {function}          [returns a D3 line constructor]
   */
  createLine(xField, yField, x, y) {
    const xAxisFormat = this.xAxis.format || 'mm/dd/yyyy';

    return d3.line()
      .curve(d3.curveCardinal)
      .x((d) => {
        if (xAxisFormat.toLowerCase() === 'timestamp') {
          return x(new Date(d[xField]));
        }
        return x(new Date(`${d[xField]} 00:00:00`));
      })
      .y(d => y(d[yField]));
  } // createLines

  /**
   * This utility method us used to create an D3 line series constructor
   * This method relies on the calcy() utility method to create a stacked line chart
   * @param  {array} xField [field to be used for x value ]
   * @param  {array} yField [field to be used for y value ]
   * @param  {object} x [x scale]
   * @param  {object} y [y scale]
   * @return {function}          [returns a D3 line constructor]
   */
  createArea(xField, yField, x, y) {
    const xAxisFormat = this.xAxis.format || 'mm/dd/yyyy';

    return d3.area()
      .curve(d3.curveCardinal)
      .x((d) => {
        if (xAxisFormat.toLowerCase() === 'timestamp') {
          return x(new Date(d[xField]));
        }
        return x(new Date(`${d[xField]} 00:00:00`));
      })
      .y1(d => y(d[yField]));
  } // createArea

  /**
   * This method is used to create an area gradient
   * @param {*} svg
   * @param {*} startColor
   * @param {*} endColor
   */
  createGradient(svg, startColor, endColor) {
    // definition for area chart color gradient
    const defs = svg.append('defs');

    // define gradient
    const gradient = defs.append('linearGradient')
        .attr('id', 'svgGradient') // unique string appended to allow each chart to have a custom gradient if desired
        .attr('x1', '0%')
        .attr('x2', '0%')
        .attr('y1', '0%')
        .attr('y2', '100%');

    gradient.append('stop')
        .attr('class', 'start')
        .attr('offset', '0%')
        .attr('stop-color', startColor)
        .attr('stop-opacity', 1);

    gradient.append('stop')
        .attr('class', 'end')
        .attr('offset', '100%')
        .attr('stop-color', endColor)
        .attr('stop-opacity', 1);
    return this;
  } // createGradient
}
